import type { ReactNode } from "react";
import { Icon } from "./Icon";
import type { ToastContent } from "../../lib/toast";
import { cn } from "../../utils/cn";

export type ToastProps = {
  title: string;
  description?: ToastContent;
  icon?: ReactNode;
  className?: string;
  titleClassName?: string;
  descriptionClassName?: string;
  iconClassName?: string;
  onClose?: () => void;
  progressTotalMs?: number;
  progressRemainingMs?: number;
  paused?: boolean;
  progressClassName?: string;
  progressTrackClassName?: string;
};

const renderInlineCode = (text: string) => {
  const parts = text.split(/(`[^`]+`)/g).filter(Boolean);
  return parts.map((part, index) => {
    if (part.startsWith("`") && part.endsWith("`")) {
      const codeText = part.slice(1, -1);
      return (
        <code key={`code-${index}`} className="toast-inline-code">
          {codeText}
        </code>
      );
    }
    return <span key={`text-${index}`}>{part}</span>;
  });
};

const renderDescription = (description: ToastContent, className?: string) => {
  if (typeof description === "string") {
    const paragraphs = description
      .split(/\n{2,}/)
      .map((segment) => segment.trim())
      .filter(Boolean);

    return (
      <div className={cn("toast-content space-y-1", className)}>
        {paragraphs.map((paragraph, index) => (
          <p key={`paragraph-${index}`} className="text-xs">
            {renderInlineCode(paragraph)}
          </p>
        ))}
      </div>
    );
  }

  if (Array.isArray(description)) {
    return (
      <ul className={cn("toast-content toast-list", className)}>
        {description.map((item, index) => (
          <li key={`item-${index}`} className="text-xs">
            {renderInlineCode(item)}
          </li>
        ))}
      </ul>
    );
  }

  return <div className={cn("toast-content", className)}>{description}</div>;
};

export const Toast = ({
  title,
  description,
  icon,
  className,
  titleClassName,
  descriptionClassName,
  iconClassName,
  onClose,
  progressTotalMs,
  progressRemainingMs,
  paused = false,
  progressClassName,
  progressTrackClassName
}: ToastProps) => {
  const totalDuration = progressTotalMs ?? 0;
  const remainingDuration = progressRemainingMs ?? totalDuration;
  const elapsedDuration = Math.max(0, totalDuration - remainingDuration);

  return (
    <div
      className={cn(
        "relative flex items-start gap-3 rounded-sm border border-border/70 p-4 shadow-soft",
        className
      )}
    >
      {icon && <div className={cn("text-primary", iconClassName)}>{icon}</div>}
      <div className="flex-1">
        <p className={cn("text-sm font-semibold", titleClassName)}>{title}</p>
        {description && renderDescription(description, descriptionClassName)}
      </div>
      {onClose && (
        <button
          type="button"
          onClick={onClose}
          className="ml-2 inline-flex h-7 w-7 items-center justify-center rounded-md border border-transparent text-inherit transition-colors hover:bg-white/15 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-white/40"
          aria-label="Dismiss notification"
        >
          <Icon icon="lucide:x" className="h-4 w-4" />
        </button>
      )}
      {totalDuration > 0 && (
        <div
          className={cn(
            "pointer-events-none absolute inset-x-0 bottom-0 h-1 overflow-hidden rounded-b-sm",
            progressTrackClassName
          )}
          aria-hidden="true"
        >
          <div
            className={cn("toast-progress", progressClassName)}
            style={{
              animationDuration: `${Math.max(0, totalDuration)}ms`,
              animationDelay: `-${Math.max(0, elapsedDuration)}ms`,
              animationPlayState: paused ? "paused" : "running"
            }}
          />
        </div>
      )}
    </div>
  );
};
